import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const normalized = String(value).trim().replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${hh}:${mm}`;
};

const parseLocalDateInput = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const addDurationLocal = (base, { days = 0, months = 0 }) => {
  const d = base ? new Date(base) : new Date();
  const date = Number.isNaN(d.getTime()) ? new Date() : d;
  if (days) {
    date.setDate(date.getDate() + days);
  }
  if (months) {
    date.setMonth(date.getMonth() + months);
  }
  return toDateTimeLocalValue(date.toISOString());
};

const plusThreeDaysLocal = (base) => addDurationLocal(base, { days: 3 });
const plusOneWeekLocal = (base) => addDurationLocal(base, { days: 7 });
const plusOneMonthLocal = (base) => {
  const d = base ? new Date(base) : new Date();
  const date = Number.isNaN(d.getTime()) ? new Date() : d;
  date.setMonth(date.getMonth() + 1);
  return toDateTimeLocalValue(date.toISOString());
};

const formatStatus = (row) => {
  if (row?.cancelYn === 'Y') return 'cancelled';
  if (row?.expiresAt && new Date(String(row.expiresAt).replace(' ', 'T')).getTime() < Date.now()) return 'expired';
  if (row?.seenYn === 'Y') return 'seen';
  return 'unseen';
};

const InviteCodeHistoryDetailPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const row = config?.initialData?.row || {};
  const canManage = Boolean(config?.initialData?.canManage);
  const [expiresAtLocal, setExpiresAtLocal] = useState(() => {
    const fromRow = toDateTimeLocalValue(row?.expiresAt);
    if (fromRow) return fromRow;
    return plusThreeDaysLocal(new Date());
  });
  const [errorMessage, setErrorMessage] = useState('');

  const senderText = useMemo(() => {
    const senderName = row?.senderName || '-';
    return row?.senderUserId ? `${senderName} (${row.senderUserId})` : senderName;
  }, [row?.senderName, row?.senderUserId]);

  const handleExtend = () => {
    if (!canManage) return;
    if (!expiresAtLocal) {
      setErrorMessage(t('popup.inviteCodeHistoryDetailPopup.errorEnterExpireAt'));
      return;
    }
    const parsed = new Date(expiresAtLocal);
    if (Number.isNaN(parsed.getTime())) {
      setErrorMessage(t('popup.inviteCodeHistoryDetailPopup.errorInvalidExpireAt'));
      return;
    }
    setErrorMessage('');
    onClose(true, {
      action: 'extend',
      uid: row?.uid,
      expiresAt: parsed.toISOString(),
    });
  };

  const handleDeactivate = () => {
    if (!canManage) return;
    onClose(true, {
      action: 'cancel',
      uid: row?.uid,
    });
  };

  return (
    <PopupWrapper
      config={{
        ...config,
        title: config?.title || t('popup.inviteCodeHistoryDetailPopup.title'),
        width: config?.width ?? 760,
        maxWidth: config?.maxWidth ?? '92vw',
      }}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-invite-code-history-detail"
    >
      <div className="main-popup-body">
        <div className="border rounded-3">
          <div className="card-body p-3 p-md-4">
            <div className="row g-2 g-md-3">
              <div className="col-12 col-md-6">
                <div className="invite-history-detail-item h-100">
              <span className="invite-history-detail-label">{t('popup.inviteCodeHistoryDetailPopup.type')}</span>
              <strong className="invite-history-detail-value">{row?.linkedTypeLabel || '-'}</strong>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="invite-history-detail-item h-100">
                  <span className="invite-history-detail-label">{t('popup.inviteCodeHistoryDetailPopup.status')}</span>
                  <strong className="invite-history-detail-value">{t(`popup.inviteCodeHistoryDetailPopup.statusValue.${formatStatus(row)}`)}</strong>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="invite-history-detail-item h-100">
                  <span className="invite-history-detail-label">{t('popup.inviteCodeHistoryDetailPopup.sender')}</span>
                  <span className="invite-history-detail-value">{senderText}</span>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="invite-history-detail-item h-100">
                  <span className="invite-history-detail-label">{t('popup.inviteCodeHistoryDetailPopup.receiver')}</span>
                  <span className="invite-history-detail-value">{row?.targetEmail || '-'}</span>
                </div>
              </div>
              <div className="col-12">
                <div className="invite-history-detail-item h-100">
                  <span className="invite-history-detail-label">{t('popup.inviteCodeHistoryDetailPopup.linkedTarget')}</span>
                  <span className="invite-history-detail-value">{row?.linkedTitle || `${row?.refTableNm || '-'}${row?.refUid ? ` #${row.refUid}` : ''}`}</span>
                </div>
              </div>
              <div className="col-12">
                <div className="invite-history-detail-item h-100">
                  <span className="invite-history-detail-label">{t('popup.inviteCodeHistoryDetailPopup.message')}</span>
                  <div className="invite-history-detail-message">
                    {row?.detailMessage ? row.detailMessage : t('popup.inviteCodeHistoryDetailPopup.noSavedMessage')}
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="invite-history-detail-item h-100">
                  <span className="invite-history-detail-label">{t('popup.inviteCodeHistoryDetailPopup.sentAt')}</span>
                  <span className="invite-history-detail-value">{row?.regDt || '-'}</span>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="invite-history-detail-item h-100">
                  <span className="invite-history-detail-label">{t('popup.inviteCodeHistoryDetailPopup.expireAt')}</span>
                  <span className="invite-history-detail-value">{row?.expiresAt || '-'}</span>
                </div>
              </div>
              <div className="col-12">
                <div className="invite-history-detail-item h-100">
                  <span className="invite-history-detail-label">{t('popup.inviteCodeHistoryDetailPopup.inviteCode')}</span>
                  <span className="invite-history-detail-value">{row?.inviteCode || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {canManage ? (
          <div className="invite-history-detail-admin-box">
            <label className="form-label small mb-1">{t('popup.inviteCodeHistoryDetailPopup.editExpireAt')}</label>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <input
                type="datetime-local"
                className="form-control"
                value={expiresAtLocal}
                onChange={(e) => {
                  setExpiresAtLocal(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
              />
            </div>
            <div className="invite-history-extend-quick mt-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  const base = parseLocalDateInput(expiresAtLocal) || new Date();
                  setExpiresAtLocal(plusThreeDaysLocal(base));
                }}
              >
                {t('popup.inviteCodeHistoryDetailPopup.plus3Days')}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  const base = parseLocalDateInput(expiresAtLocal) || new Date();
                  setExpiresAtLocal(plusOneWeekLocal(base));
                }}
              >
                {t('popup.inviteCodeHistoryDetailPopup.plus1Week')}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  const base = parseLocalDateInput(expiresAtLocal) || new Date();
                  setExpiresAtLocal(plusOneMonthLocal(base));
                }}
              >
                {t('popup.inviteCodeHistoryDetailPopup.plus1Month')}
              </button>
            </div>
            {errorMessage ? <p className="small text-danger mt-2 mb-0">{errorMessage}</p> : null}
            <p className="small text-muted mt-2 mb-0">
              {t('popup.inviteCodeHistoryDetailPopup.defaultExpireNote')}
            </p>
            <p className="small text-muted mt-1 mb-0">
              {t('popup.inviteCodeHistoryDetailPopup.cancelInviteNote')}
            </p>
          </div>
        ) : null}
      </div>
      {canManage ? (
        <div className="main-popup-footer d-flex justify-content-end">
          <div className="d-flex gap-2">
            <button
              type="button"
              className={`btn ${getButtonClass('btn-outline-danger')}`}
              onClick={handleDeactivate}
              disabled={row?.cancelYn === 'Y'}
            >
              {row?.cancelYn === 'Y' ? t('popup.inviteCodeHistoryDetailPopup.alreadyCancelled') : t('popup.inviteCodeHistoryDetailPopup.cancelInvite')}
            </button>
            <button type="button" className={`btn main-popup-right-btn ${getButtonClass('btn-primary')}`} onClick={handleExtend}>
              {t('popup.inviteCodeHistoryDetailPopup.applyExpireAt')}
            </button>
          </div>
        </div>
      ) : null}
      <style>{`
        .popup-invite-code-history-detail .invite-history-detail-card {
          border-radius: 14px;
          background: #fff;
        }
        .popup-invite-code-history-detail .invite-history-detail-item {
          background: transparent;
        }
        .popup-invite-code-history-detail .invite-history-detail-label {
          display: block;
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 4px;
        }
        .popup-invite-code-history-detail .invite-history-detail-value {
          font-size: 14px;
          color: #111827;
          word-break: break-word;
        }
        .popup-invite-code-history-detail .invite-history-detail-message {
          min-height: 72px;
          white-space: pre-wrap;
          color: #111827;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 10px;
        }
        .popup-invite-code-history-detail .invite-history-detail-admin-box {
          margin-top: 14px;
          border: 1px solid #dbeafe;
          background: linear-gradient(180deg, #f8fbff 0%, #f3f8ff 100%);
          border-radius: 10px;
          padding: 12px;
        }
        .popup-invite-code-history-detail .invite-history-extend-quick {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .popup-invite-code-history-detail .invite-history-extend-quick .btn {
          min-width: 88px;
          border-radius: 999px;
          font-weight: 600;
        }
      `}</style>
    </PopupWrapper>
  );
};

export default InviteCodeHistoryDetailPopup;
